import { Check, Lock, Users } from 'lucide-react'
import { useId, type ReactNode } from 'react'
import { useSharePlaces, type Audience, type Place } from '@/lib/spaces'
import { useMe } from '@/lib/useMe'
import { cn } from '@/lib/utils'
import { Loader } from '@/components/Loader'

// "Who's this for?", the same everywhere: a photo, recipe, moment, album, recipe book or cookbook is kept just for you,
// shared with Everyone (everyone in every circle you're in and everyone you're connected to, now and later), or shared
// with any spaces you're in, in any of your circles. With one circle the spaces are one choice at a time, as ever; with
// more, each circle is its own group to tick. `here` is the circle you're in, listed first. Everyone is offered once
// you're in a circle or connected with someone, before anyone joins: whoever joins or connects later sees it too.
export function ShareWith({
  value,
  onChange,
  here,
  onlyMeHint,
}: {
  value: Audience
  onChange: (audience: Audience) => void
  here: string
  onlyMeHint: string
}) {
  const name = useId()
  const places = useSharePlaces()
  const offered = useMe().data?.everyone ?? true
  if (!places.data) return <Loader label="Finding your circles" />
  const circles = byCircle(places.data, here)
  const picked = new Set(value.everyone ? [] : value.spaceIds)
  const one = circles.length === 1
  const top = (
    <>
      <PlaceChoice
        type="radio"
        name={name}
        checked={!value.everyone && picked.size === 0}
        onChange={() => onChange({ everyone: false, spaceIds: [] })}
        title={
          <>
            <Lock className="size-3.5" aria-hidden />
            Only me
          </>
        }
        hint={onlyMeHint}
      />
      {(offered || value.everyone) && (
        <PlaceChoice
          type="radio"
          name={name}
          checked={value.everyone}
          onChange={() => onChange({ everyone: true, spaceIds: [] })}
          title={
            <>
              <Users className="size-3.5" aria-hidden />
              Everyone
            </>
          }
          hint={
            circles.length === 0
              ? 'Everyone you’re connected to, now and later'
              : one
                ? 'Everyone in your circle and your connections, now and later'
                : 'Everyone in all your circles and your connections, now and later'
          }
        />
      )}
    </>
  )

  if (one) {
    return (
      <fieldset className="grid gap-2">
        <legend className="sr-only">Who’s this for?</legend>
        {top}
        {circles[0]!.places.map((x) => (
          <PlaceChoice
            key={x.id}
            type="radio"
            name={name}
            checked={!value.everyone && picked.size === 1 && picked.has(x.id)}
            onChange={() => onChange({ everyone: false, spaceIds: [x.id] })}
            title={x.name}
            hint={hintFor(x)}
          />
        ))}
      </fieldset>
    )
  }

  const toggle = (id: string) =>
    onChange({ everyone: false, spaceIds: picked.has(id) ? [...picked].filter((x) => x !== id) : [...picked, id] })
  return (
    <div className="grid gap-5">
      <fieldset className="grid gap-2">
        <legend className="sr-only">Who’s this for?</legend>
        {top}
      </fieldset>
      {circles.map((circle) => (
        <fieldset key={circle.familyId} className="grid gap-2">
          <legend className="mb-1 text-sub font-medium text-muted-foreground">{circle.circle}</legend>
          {circle.places.map((x) => (
            <PlaceChoice
              key={x.id}
              type="checkbox"
              checked={picked.has(x.id)}
              onChange={() => toggle(x.id)}
              title={x.name}
              hint={hintFor(x)}
            />
          ))}
        </fieldset>
      ))}
    </div>
  )
}

const hintFor = (x: Place) => (x.kind === 'everyone' ? `Everyone in ${x.circle}` : `Only the people in ${x.name}`)

// Places by circle, the one you're in first, then the rest in the order you joined them.
function byCircle(places: Place[], here: string) {
  const circles: { familyId: string; circle: string; places: Place[] }[] = []
  for (const x of places) {
    const found = circles.find((c) => c.familyId === x.familyId)
    if (found) found.places.push(x)
    else circles.push({ familyId: x.familyId, circle: x.circle, places: [x] })
  }
  return circles.sort((a, b) => Number(b.familyId === here) - Number(a.familyId === here))
}

function PlaceChoice({
  type,
  name,
  checked,
  onChange,
  title,
  hint,
}: {
  type: 'radio' | 'checkbox'
  name?: string
  checked: boolean
  onChange: () => void
  title: ReactNode
  hint: string
}) {
  return (
    <label className="flex min-h-16 cursor-pointer items-center gap-4 rounded-2xl border border-border px-4 py-3 transition-colors hover:bg-secondary has-checked:border-brand has-checked:bg-secondary has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50">
      <input type={type} name={name} className="sr-only" checked={checked} onChange={onChange} />
      <span className="flex flex-1 flex-col">
        <span className="flex items-center gap-1.5 font-medium">{title}</span>
        <span className="text-sub text-muted-foreground">{hint}</span>
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'grid size-6 place-items-center border-2',
          type === 'radio' ? 'rounded-full' : 'rounded-md',
          checked ? 'border-brand bg-brand text-brand-foreground' : 'border-input',
        )}
      >
        {checked && <Check className="size-3.5" strokeWidth={3} />}
      </span>
    </label>
  )
}
